import { useMemo, useState } from 'react';

import { useGetCourseCategoriesQuery, useUpdateTranscriptMutation } from '@teachur/http-client';

import type { TranscriptBreakdown } from '@new/features/transcripts/shared/types';
import {
  canSaveReview,
  makeKeyGen,
  normalizeGrade,
  toCoursePayload,
  useReviewEdits,
  type ReviewState,
} from './reviewState';

const nextKey = makeKeyGen('e');

const toReviewState = (transcript: TranscriptBreakdown): ReviewState => ({
  institution: transcript.institution,
  institutionUrl: transcript.institutionUrl ?? '',
  accreditingOrganizations: transcript.accreditingOrganizations ?? [],
  awardedDegree: transcript.awardedDegree ?? null,
  officialTranscriptRequested: transcript.officialTranscriptRequested ?? false,
  unofficialCredits: transcript.unofficialCredits,
  unofficialFileId: transcript.files.find((f) => f.kind === 'unofficial')?.fileId ?? null,
  courses: transcript.courses.map((c) => ({
    key: nextKey(),
    rawName: c.rawName,
    rawCode: c.rawCode ?? null,
    creditHours: c.creditHours == null ? null : Math.round(Number(c.creditHours)),
    grade: normalizeGrade(c.grade ?? null),
    categoryIds: c.categoryIds ?? [],
  })),
});

export const useEditTranscript = (
  transcript: TranscriptBreakdown,
  options?: { onDone?: () => void }
) => {
  const [step, setStep] = useState<'review' | 'success'>('review');
  const [review, setReview] = useState<ReviewState>(() => toReviewState(transcript));
  const update = useUpdateTranscriptMutation();
  const categoriesQuery = useGetCourseCategoriesQuery();
  const categories = categoriesQuery.data ?? [];

  const edits = useReviewEdits(setReview, nextKey);

  const save = async () => {
    if (review.unofficialCredits === null) {
      return;
    }
    await update.mutateAsync({
      id: transcript.id,
      body: {
        institution: review.institution,
        institutionUrl: review.institutionUrl || null,
        unofficialCredits: review.unofficialCredits,
        courses: toCoursePayload(review.courses),
        officialTranscriptRequested: review.officialTranscriptRequested,
      },
    });
    setStep('success');
    options?.onDone?.();
  };

  const canSave = useMemo(() => canSaveReview(review), [review]);

  return {
    step,
    method: 'manual' as const,
    review,
    aiError: null as string | null,
    categories,
    isUploading: false,
    isSaving: update.isPending,
    canSave,
    canGoBack: false,
    goBack: () => {},
    reset: () => {},
    submitFile: async () => {},
    ...edits,
    save,
  };
};

export type EditTranscript = ReturnType<typeof useEditTranscript>;
